import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Button, Card, Dropdown, Form, Icon, Input, Modal, Tab, TextArea } from 'semantic-ui-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToasts } from 'common/toast';
import { Container } from 'common/layouts/AuthLayout/PageTitle';
import { PageHeader } from 'common/styles/page';
import { RecommendationType } from '../../../../common/constants/RecommendationType';
import { templatesApi } from '../api/recommendation-templates';
import cloneDeep from 'lodash/cloneDeep';
import LoadingSpinner from 'common/components/LoadingSpinner';
import { PILLARS } from 'common/constants/pillars';
import RepositoryContentListModal from '../../repository-management/components/RepositoryContentListModal';
import {
  HeaderCell,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from 'common/components/Table';
import {
  StyledIconButton,
  StyledTrashButton,
  StyledCancelButton,
  StyledSaveButton,
  StyledAddButton,
} from 'common/components/button/StyledButton';
import { isYouTubeWatchUrl } from 'common/utils/resourceLinks';

const newEntityState = () => ({
  title: '',
  tasks: [],
  factor: null,
});

const Intervention = () => {
  const navigate = useNavigate();
  const { id: templateId } = useParams();
  const { addToast } = useToasts();

  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [editingTaskIndex, setEditingTaskIndex] = useState<number | null>(null);
  const [taskFormData, setTaskFormData] = useState({
    task: '',
    themes: '',
    challenge: '',
    description: '',
    practicalExample: '',
    softSkills: '',
    time: '',
    keywords: '',
    references: '',
    supportingScience: '',
    // Learn More fields
    areaOfFocus: '',
    howToImplement: '',
    whatNotToDo: '',
    howToKnowItsWorking: '',
    suggestedProcessChanges: '',
    processChangeImplementationTime: '',
  });
  const [isFileSelectOpen, setIsFileSelectOpen] = useState(false);

  const [interventionProject, setInterventionProject] = useState<
    | {
        title: string;
        tasks: any[];
        factor: any;
      }
    | undefined
  >(undefined);

  useEffect(() => {
    if (!templateId) {
      setInterventionProject(newEntityState());
    }
  }, [templateId]);

  const { data: existingProject, isLoading: projectIsLoading } =
    templatesApi.useApiAdminV1RecommendationTemplateRetrieveQuery(
      {
        id: Number(templateId),
      },
      { skip: !templateId }
    );

  const [triggerUpdate] = templatesApi.useApiAdminV1RecommendationTemplateUpdateMutation();

  const [triggerInsert] = templatesApi.useApiAdminV1RecommendationTemplateCreateMutation();

  useEffect(() => {
    if (existingProject) {
      setInterventionProject({
        title: existingProject.name,
        ...cloneDeep(existingProject.template),
      });
    }
  }, [existingProject]);

  const saveIntervention = () => {
    if (!interventionProject) {
      return;
    }
    const updateId = templateId || null;
    const pillar = PILLARS.find(p => p.id === interventionProject.factor);
    const pillarName = pillar ? pillar.name : 'Intervention';

    const templatePayload = {
      id: updateId || null,
      name: pillarName,
      status: 9,
      template: interventionProject,
      type_of: RecommendationType.INTERVENTIONS,
      target: 1,
    };

    if (updateId) {
      triggerUpdate({
        id: templatePayload.id,
        recommendationTemplate: templatePayload,
      } as any);
    } else {
      triggerInsert({
        id: templatePayload.id,
        recommendationTemplate: templatePayload,
      } as any);
    }

    addToast('Intervention Template Saved', {
      appearance: 'success',
      autoDismiss: true,
      autoDismissTimeout: 1500,
    });
    navigate('/settings/admin/recommendation-templates/interventions');
  };

  const openTaskModal = (taskIndex: number | null = null) => {
    if (taskIndex !== null && interventionProject) {
      const task = interventionProject.tasks[taskIndex];
      setTaskFormData({
        task: task.task || '',
        themes: task.themes || '',
        challenge: task.challenge || '',
        description: task.description || '',
        practicalExample: task.practicalExample || '',
        softSkills: task.softSkills || '',
        time: task.time || '',
        keywords: task.keywords || '',
        references: task.references || '',
        supportingScience: task.supportingScience || '',
        areaOfFocus: task.areaOfFocus || '',
        howToImplement: task.howToImplement || '',
        whatNotToDo: task.whatNotToDo || '',
        howToKnowItsWorking: task.howToKnowItsWorking || '',
        suggestedProcessChanges: task.suggestedProcessChanges || '',
        processChangeImplementationTime: task.processChangeImplementationTime || '',
      });
      setEditingTaskIndex(taskIndex);
    } else {
      setTaskFormData({
        task: '',
        themes: '',
        challenge: '',
        description: '',
        practicalExample: '',
        softSkills: '',
        time: '',
        keywords: '',
        references: '',
        supportingScience: '',
        areaOfFocus: '',
        howToImplement: '',
        whatNotToDo: '',
        howToKnowItsWorking: '',
        suggestedProcessChanges: '',
        processChangeImplementationTime: '',
      });
      setEditingTaskIndex(null);
    }
    setTaskModalOpen(true);
  };

  const closeTaskModal = () => {
    setTaskModalOpen(false);
    setEditingTaskIndex(null);
    setTaskFormData({
      task: '',
      themes: '',
      challenge: '',
      description: '',
      practicalExample: '',
      softSkills: '',
      time: '',
      keywords: '',
      references: '',
      supportingScience: '',
      areaOfFocus: '',
      howToImplement: '',
      whatNotToDo: '',
      howToKnowItsWorking: '',
      suggestedProcessChanges: '',
      processChangeImplementationTime: '',
    });
  }; 

  const saveTask = () => {
    if (!interventionProject) {
      return;
    }
    const updatedTasks = [...interventionProject.tasks];
    if (editingTaskIndex !== null) {
      // Edit existing task - preserve resources
      const updatedTask = {
        ...updatedTasks[editingTaskIndex],
        task: taskFormData.task,
        themes: taskFormData.themes,
        challenge: taskFormData.challenge,
        description: taskFormData.description,
        practicalExample: taskFormData.practicalExample,
        softSkills: taskFormData.softSkills,
        time: taskFormData.time,
        keywords: taskFormData.keywords,
        references: taskFormData.references,
        supportingScience: taskFormData.supportingScience,
        // Learn More fields
        areaOfFocus: taskFormData.areaOfFocus,
        howToImplement: taskFormData.howToImplement,
        whatNotToDo: taskFormData.whatNotToDo,
        howToKnowItsWorking: taskFormData.howToKnowItsWorking,
        suggestedProcessChanges: taskFormData.suggestedProcessChanges,
        processChangeImplementationTime: taskFormData.processChangeImplementationTime,
      };
      // remove legacy properties
      delete (updatedTask as any).type;
      delete (updatedTask as any).method;
      updatedTasks[editingTaskIndex] = updatedTask;
    } else {
      // Add new task
      updatedTasks.push({
        task: taskFormData.task,
        themes: taskFormData.themes,
        challenge: taskFormData.challenge,
        description: taskFormData.description,
        practicalExample: taskFormData.practicalExample,
        softSkills: taskFormData.softSkills,
        time: taskFormData.time,
        keywords: taskFormData.keywords,
        references: taskFormData.references,
        supportingScience: taskFormData.supportingScience,
        areaOfFocus: taskFormData.areaOfFocus,
        howToImplement: taskFormData.howToImplement,
        whatNotToDo: taskFormData.whatNotToDo,
        howToKnowItsWorking: taskFormData.howToKnowItsWorking,
        suggestedProcessChanges: taskFormData.suggestedProcessChanges,
        processChangeImplementationTime: taskFormData.processChangeImplementationTime,
        content: [],
      });
    }
    setInterventionProject({ ...interventionProject, tasks: updatedTasks });
    closeTaskModal();
  };

  const removeTask = (taskIndex: number) => {
    if (!interventionProject) {
      return;
    }
    const updatedTasks = interventionProject.tasks.filter((_: any, i: number) => i !== taskIndex);
    setInterventionProject({ ...interventionProject, tasks: updatedTasks });
  };

  const addResource = (newResource: any) => {
    if (!interventionProject || editingTaskIndex === null) {
      console.warn('Cannot add resource without task index');
      return;
    }
    const updatedTasks = [...interventionProject.tasks];
    const currentResources = updatedTasks[editingTaskIndex]['content'] || [];
    updatedTasks[editingTaskIndex]['content'] = [...currentResources, newResource];
    setInterventionProject({ ...interventionProject, tasks: updatedTasks });
  };

  const removeResource = (resourceIndex: number) => {
    if (!interventionProject || editingTaskIndex === null) {
      console.warn('Cannot remove resource without task index');
      return;
    }
    const updatedTasks = [...interventionProject.tasks];
    const currentResources = updatedTasks[editingTaskIndex]['content'] || [];
    updatedTasks[editingTaskIndex]['content'] = currentResources.filter((_: any, i: number) => i !== resourceIndex);
    setInterventionProject({ ...interventionProject, tasks: updatedTasks });
  };

  const isYouTubeResource = (resource: any) =>
    isYouTubeWatchUrl(resource?.object_key || resource?.url || '');

  if (projectIsLoading || !interventionProject) {
    return <LoadingSpinner />;
  }

  return (
    <Container>
      <PageHeader
        title="Intervention Template"
        controls={(
          <ButtonGroup>
            <StyledCancelButton
              onClick={() => navigate('/settings/admin/recommendation-templates/interventions')}
              label="Cancel"
              width="120px"
            />
            <StyledSaveButton
              onClick={saveIntervention}
              label="Save"
              width="120px"
            />
          </ButtonGroup>
        )}
      />
      <StyledCard>
        <Card fluid>
          <StyledCardHeader>
            <Card.Header>Pillar</Card.Header>
          </StyledCardHeader>
          <Card.Content>
            <Form>
              <Form.Field>
                <Dropdown
                  fluid
                  selection
                  options={PILLARS.map(pillar => ({
                    key: pillar.id,
                    text: pillar.name,
                    value: pillar.id,
                  }))}
                  value={interventionProject.factor}
                  onChange={(e, { value }) => {
                    setInterventionProject({
                      ...interventionProject,
                      factor: value,
                    });
                  }}
                />
              </Form.Field>
            </Form>
          </Card.Content>
        </Card>
      </StyledCard>

      <StyledCard style={{ marginTop: '20px' }}>
        <Card fluid>
          <StyledCardHeader>
            <TasksHeader>
              <Card.Header><h3>Tasks</h3></Card.Header>
              <StyledAddButton
                onClick={() => openTaskModal()}
                label="Add Task"
                width="120px"
              />
            </TasksHeader>
          </StyledCardHeader>
          <Card.Content>
            <Table>
              <TableHeader>
                <TableRow>
                  <HeaderCell width={5}>Recommendation</HeaderCell>
                  <HeaderCell width={6}>Description</HeaderCell>
                  <HeaderCell width={3}>Time</HeaderCell>
                  <HeaderCell width={1} justify="center">Actions</HeaderCell>
                </TableRow>
              </TableHeader>
            <TableBody>
              {interventionProject.tasks.map((task: any, index: number) => (
                <TableRow 
                  key={index}
                  onClick={() => openTaskModal(index)}
                  style={{ cursor: 'pointer' }}
                >
                  <TableCell>{task.task || '-'}</TableCell>
                  <TableCell>
                    {task.description ? (
                      <div title={task.description}>
                        {task.description.length > 100
                          ? `${task.description.slice(0, 100)}...`
                          : task.description}
                      </div>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>{task.time || '-'}</TableCell>
                  <TableCell justify="center">
                    <ButtonGroup>
                      <StyledIconButton
                        icon="edit"
                        onClick={(e: { stopPropagation: () => void; }) => {
                          e.stopPropagation();
                          openTaskModal(index);
                        }}
                        title="Edit task"
                      />
                      <StyledTrashButton
                        onClick={(id: number) => removeTask(id)}
                        id={index}
                        title="Delete task"
                      />
                    </ButtonGroup>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </Card.Content>
        </Card>
      </StyledCard>

      <Modal open={taskModalOpen} onClose={closeTaskModal} size="large" style={{ minWidth: '720px' }}>
        <Modal.Header>{editingTaskIndex !== null ? 'Edit Task' : 'Add Task'}</Modal.Header>
        <Modal.Content style={{ minHeight: '720px' }}>
          <Tab
            panes={[
              {
                menuItem: 'Task Details',
                render: () => (
                  <Tab.Pane>
                    <Form>
                      <Form.Field>
                        <label>Recommendation</label>
                        <Input
                          fluid
                          value={taskFormData.task}
                          onChange={(e) => setTaskFormData({ ...taskFormData, task: e.target.value })}
                          placeholder="Recommendation description"
                        />
                      </Form.Field>
                      <Form.Field>
                        <label>Description</label>
                        <TextArea
                          value={taskFormData.description}
                          onChange={(e) => setTaskFormData({ ...taskFormData, description: e.target.value })}
                          placeholder="Description"
                          rows={3}
                        />
                      </Form.Field>
                      <Form.Field>
                        <label>Challenge</label>
                        <Input
                          fluid
                          value={taskFormData.challenge}
                          onChange={(e) => setTaskFormData({ ...taskFormData, challenge: e.target.value })}
                          placeholder="Challenge"
                        />
                      </Form.Field>
                      <Form.Field>
                        <label>Practical Example</label>
                        <Input
                          fluid
                          value={taskFormData.practicalExample}
                          onChange={(e) => setTaskFormData({ ...taskFormData, practicalExample: e.target.value })}
                          placeholder="Practical example"
                        />
                      </Form.Field>
                      <Form.Group widths="equal">
                        <Form.Field>
                          <label>Themes</label>
                          <Input
                            fluid
                            value={taskFormData.themes}
                            onChange={(e) => setTaskFormData({ ...taskFormData, themes: e.target.value })}
                            placeholder="Key themes"
                          />
                        </Form.Field>
                        <Form.Field>
                          <label>Time</label>
                          <Input
                            fluid
                            value={taskFormData.time}
                            onChange={(e) => setTaskFormData({ ...taskFormData, time: e.target.value })}
                            placeholder="Time required"
                          />
                        </Form.Field>
                      </Form.Group>
                      <Form.Group widths="equal">
                        <Form.Field>
                          <label>Soft Skills</label>
                          <Input
                            fluid
                            value={taskFormData.softSkills}
                            onChange={(e) => setTaskFormData({ ...taskFormData, softSkills: e.target.value })}
                            placeholder="Soft skills"
                          />
                        </Form.Field>
                        <Form.Field>
                          <label>Keywords</label>
                          <Input
                            fluid
                            value={taskFormData.keywords}
                            onChange={(e) => setTaskFormData({ ...taskFormData, keywords: e.target.value })}
                            placeholder="Keywords"
                          />
                        </Form.Field>
                      </Form.Group>
                      <Form.Group widths="equal">
                        <Form.Field>
                          <label>References</label>
                          <Input
                            fluid
                            value={taskFormData.references}
                            onChange={(e) => setTaskFormData({ ...taskFormData, references: e.target.value })}
                            placeholder="References"
                          />
                        </Form.Field>
                        <Form.Field>
                          <label>Supporting Science</label>
                          <Input
                            fluid
                            value={taskFormData.supportingScience}
                            onChange={(e) => setTaskFormData({ ...taskFormData, supportingScience: e.target.value })}
                            placeholder="Supporting science"
                          />
                        </Form.Field>
                      </Form.Group>
                    </Form>
                  </Tab.Pane>
                ),
              },
              {
                menuItem: 'Learn More',
                render: () => (
                  <Tab.Pane>
                    <Form>
                      <Form.Field>
                        <label>Area of focus</label>
                        <Input
                          fluid
                          value={taskFormData.areaOfFocus}
                          onChange={(e) => setTaskFormData({ ...taskFormData, areaOfFocus: e.target.value })}
                          placeholder="Area of focus"
                        />
                      </Form.Field>

                      <Form.Field>
                        <label>How to implement</label>
                        <TextArea
                          value={taskFormData.howToImplement}
                          onChange={(e) => setTaskFormData({ ...taskFormData, howToImplement: (e.target as any).value })}
                          placeholder="How to implement"
                          rows={3}
                        />
                      </Form.Field>

                      <Form.Field>
                        <label>What not to do</label>
                        <TextArea
                          value={taskFormData.whatNotToDo}
                          onChange={(e) => setTaskFormData({ ...taskFormData, whatNotToDo: (e.target as any).value })}
                          placeholder="What not to do"
                          rows={3}
                        />
                      </Form.Field>

                      <Form.Field>
                        <label>How to know it's working</label>
                        <TextArea
                          value={taskFormData.howToKnowItsWorking}
                          onChange={(e) => setTaskFormData({ ...taskFormData, howToKnowItsWorking: (e.target as any).value })}
                          placeholder="How to know it's working"
                          rows={3}
                        />
                      </Form.Field>

                      <Form.Field>
                        <label>Suggested Process Changes</label>
                        <TextArea
                          value={taskFormData.suggestedProcessChanges}
                          onChange={(e) => setTaskFormData({ ...taskFormData, suggestedProcessChanges: (e.target as any).value })}
                          placeholder="Suggested process changes"
                          rows={3}
                        />
                      </Form.Field>

                      <Form.Field>
                        <label>Process Change Implementation Time</label>
                        <Input
                          fluid
                          value={taskFormData.processChangeImplementationTime}
                          onChange={(e) => setTaskFormData({ ...taskFormData, processChangeImplementationTime: e.target.value })}
                          placeholder="Implementation time (e.g., 2 weeks)"
                        />
                      </Form.Field>
                    </Form>
                  </Tab.Pane>
                ),
              },
              {
                menuItem: 'Resources',
                render: () => (
                  <Tab.Pane>
                    <ResourcesTable>
                      <thead>
                        <tr>
                          <th>Resource Text</th>
                          <th>Resource URL</th>
                          <th style={{ width: '80px' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {editingTaskIndex !== null &&
                          interventionProject?.tasks[editingTaskIndex]?.content?.map(
                            (resource: any, index: number) => (
                              <tr key={index}>
                                <td>
                                  {isYouTubeResource(resource) ? (
                                    <ResourceCellInline data-testid="youtube-resource-text-cell">
                                      <Icon name="video" />
                                      <span>{resource.name || resource.text || ''}</span>
                                    </ResourceCellInline>
                                  ) : (
                                    <Input
                                      disabled
                                      readOnly
                                      value={resource.name || resource.text || ''}
                                      style={{ width: '100%' }}
                                    />
                                  )}
                                </td>
                                <td>
                                  {isYouTubeResource(resource) ? (
                                    <ResourceCellInline data-testid="youtube-resource-url-cell">
                                      <Icon name="youtube" color="red" />
                                      <span>YouTube</span>
                                    </ResourceCellInline>
                                  ) : (
                                    <Input
                                      disabled
                                      readOnly
                                      value={resource.object_key || resource.url || ''}
                                      style={{ width: '100%' }}
                                    />
                                  )}
                                </td>
                                <td style={{ textAlign: 'center' }}>
                                  <StyledIconButton
                                    icon="trash alternate"
                                    onClick={() => removeResource(index)}
                                    title="Delete Resource"
                                    color="red"
                                  />
                                </td>
                              </tr>
                            )
                          )}
                        <tr>
                          <td colSpan={3}>
                            <Button
                              icon
                              labelPosition="left"
                              primary
                              onClick={() => setIsFileSelectOpen(true)}
                              disabled={editingTaskIndex === null}
                            >
                              <Icon name="folder open" />
                              Select from Repository
                            </Button>
                          </td>
                        </tr>
                      </tbody>
                    </ResourcesTable>
                  </Tab.Pane>
                ),
              },
            ]}
          />
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={closeTaskModal}>Cancel</Button>
          <Button primary onClick={saveTask}>
            {editingTaskIndex !== null ? 'Update' : 'Add'}
          </Button>
        </Modal.Actions>
      </Modal>

      <RepositoryContentListModal
        open={isFileSelectOpen}
        onItemClicked={(item: any) => {
          addResource({
            id: item.id,
            name: item.name,
            object_key: item.object_key,
            type: item.type,
          });
          setIsFileSelectOpen(false);
        }}
        onClose={() => setIsFileSelectOpen(false)}
      />
    </Container>
  );
};

export default Intervention;

const StyledCard = styled.div`
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
  border: 1px solid rgba(34, 36, 38, 0.15) !important;
`;

const StyledCardHeader = styled(Card.Content)`
  background-color: #f5f5f5 !important;
`;

const TasksHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 5px;

  h3 {
    margin: 0;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
`;

const ResourcesTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 10px;
    border: 1px solid #ddd;
  }

  th {
    background-color: #f5f5f5;
    font-weight: 600;
    text-align: left;
  }

  tbody tr:hover {
    background-color: #fafafa;
  }
`;

const ResourceCellInline = styled.div`
  min-height: 38px;
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #f9fafb;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #444;

  i.icon {
    margin: 0 !important;
  }
`;
